import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { NearbyRestaurantSelector } from '@/components/submission/NearbyRestaurantSelector';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Loader2, ChevronUp, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface DishLinkStepProps {
  onSelect: (dishId: string, dishName: string, restaurantName?: string, imageUrl?: string | null, cuisine?: string[] | null) => void;
  onBack: () => void;
  showBack?: boolean;
  onAddNewDish?: (restaurantData: {
    name: string;
    place_id: string;
    address?: string;
    lat?: number;
    lng?: number;
  }) => void;
  serviceType?: 'dine_in' | 'takeaway' | null;
}

interface Dish {
  id: string;
  name: string;
  vote_count: number;
  image_url: string | null;
  cuisine: string[] | null;
}

export const DishLinkStep: React.FC<DishLinkStepProps> = ({
  onSelect,
  onBack,
  showBack = true,
  onAddNewDish,
  serviceType
}) => {
  const [selectedRestaurant, setSelectedRestaurant] = useState<{
    name: string;
    place_id: string;
    restaurant_id: string | null;
    address?: string;
    lat?: number;
    lng?: number;
  } | null>(null);

  // Fetch dishes for the selected restaurant
  const { data: dishes, isLoading: dishesLoading } = useQuery({
    queryKey: ['restaurant-dishes', selectedRestaurant?.restaurant_id],
    queryFn: async () => {
      if (!selectedRestaurant?.restaurant_id) return [];
      
      const { data, error } = await supabase
        .from('dishes')
        .select('id, name, vote_count, image_url, cuisine')
        .eq('restaurant_id', selectedRestaurant.restaurant_id)
        .order('vote_count', { ascending: false });
      
      if (error) throw error;
      return data as Dish[];
    },
    enabled: !!selectedRestaurant?.restaurant_id
  });

  const handleRestaurantSelect = async (data: {
    name: string;
    place_id: string;
    address: string;
    lat: number;
    lng: number;
  }) => {
    // Look up the restaurant_id from the restaurants table using google_place_id
    const { data: restaurant } = await supabase
      .from('restaurants')
      .select('id')
      .eq('google_place_id', data.place_id)
      .maybeSingle();
    
    setSelectedRestaurant({
      name: data.name,
      place_id: data.place_id,
      restaurant_id: restaurant?.id || null,
      address: data.address,
      lat: data.lat,
      lng: data.lng
    });
  };

  const handleClearRestaurant = () => {
    setSelectedRestaurant(null);
  };

  const handleAddNewDishClick = () => {
    if (onAddNewDish && selectedRestaurant) {
      onAddNewDish({
        name: selectedRestaurant.name,
        place_id: selectedRestaurant.place_id,
        address: selectedRestaurant.address,
        lat: selectedRestaurant.lat,
        lng: selectedRestaurant.lng
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {showBack && (
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Button>
        </div>
      )}

      {/* Restaurant Search */}
      <div className="space-y-4">
        {!selectedRestaurant ? (
          <NearbyRestaurantSelector onSelect={handleRestaurantSelect} />
        ) : (
          <div className="space-y-4">
            {/* Selected Restaurant Display */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
              <span className="font-medium text-foreground">{selectedRestaurant.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearRestaurant}
                className="text-muted-foreground hover:text-foreground"
              >
                Change
              </Button>
            </div>

            {/* Dishes List */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Select a Dish
              </label>
              
              {dishesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !selectedRestaurant.restaurant_id ? (
                <div className="text-center py-8 space-y-4">
                  <p className="text-muted-foreground">This restaurant isn't in our database yet.</p>
                  <p className="text-sm text-muted-foreground">Be the first to add a dish!</p>
                  {onAddNewDish && (
                    <Button
                      onClick={handleAddNewDishClick}
                      className="bg-[#cfef00] hover:bg-[#cfef00]/90 text-black min-h-[44px]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add This Dish
                    </Button>
                  )}
                </div>
              ) : dishes && dishes.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {dishes.map((dish) => (
                    <button
                      key={dish.id}
                      onClick={() => onSelect(dish.id, dish.name, selectedRestaurant.name, dish.image_url, dish.cuisine)}
                      className="w-full flex items-center gap-3 p-3 bg-background border border-border rounded-lg hover:border-[#cfef00] hover:bg-[#cfef00]/5 transition-colors text-left"
                    >
                      {dish.image_url ? (
                        <img
                          src={dish.image_url}
                          alt={dish.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-muted flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{dish.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <ChevronUp className="h-3 w-3" />
                          {dish.vote_count} votes
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <p className="text-muted-foreground">No dishes found for this restaurant.</p>
                  <p className="text-sm text-muted-foreground">Be the first to add one!</p>
                  {onAddNewDish && (
                    <Button
                      onClick={handleAddNewDishClick}
                      className="bg-[#cfef00] hover:bg-[#cfef00]/90 text-black min-h-[44px]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add This Dish
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
